import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import Modal from "../modal/Modal";
import { createVideollamadaCase } from "./casesApi";

const ButtonAdd = styled(Button)({
  borderRadius: "20px",
  backgroundColor: "#161e55",
  color: "#fff",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#A8C8AF",
  },
  "&.Mui-disabled": {
    backgroundColor: "rgba(22, 30, 85, 0.45)",
    color: "#fff",
  },
});

const ButtonSecondary = styled(Button)({
  borderRadius: "20px",
  color: "#161e55",
  borderColor: "#161e55",
  textTransform: "none",
});

const getApiErrorMessage = (error, defaultMessage) => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (typeof error?.response?.data === "string") {
    return error.response.data;
  }
  if (error?.message) {
    return error.message;
  }
  return defaultMessage;
};

export default function VideollamadaModal({ activeModal, handleModal, onError }) {
  const [name, setName] = useState("");
  const [documentNumber, setDocumentNumber] = useState("");
  const [nameError, setNameError] = useState("");
  const [documentError, setDocumentError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionLink, setSessionLink] = useState(null);

  const resetState = () => {
    setName("");
    setDocumentNumber("");
    setNameError("");
    setDocumentError("");
    setLoading(false);
    setSessionLink(null);
  };

  useEffect(() => {
    if (!activeModal) {
      resetState();
    }
  }, [activeModal]);

  const validateForm = () => {
    const trimmedName = name.trim();
    const trimmedDocument = documentNumber.trim();
    let isValid = true;

    if (!trimmedName) {
      setNameError("El nombre es obligatorio.");
      isValid = false;
    } else {
      setNameError("");
    }

    if (!trimmedDocument) {
      setDocumentError("El número de documento es obligatorio.");
      isValid = false;
    } else {
      setDocumentError("");
    }

    return isValid ? { name: trimmedName, documentNumber: trimmedDocument } : null;
  };

  const handleGenerate = async () => {
    const validated = validateForm();
    if (!validated) {
      return;
    }

    setLoading(true);
    try {
      const response = await createVideollamadaCase(
        validated.name,
        validated.documentNumber
      );

      if (response?.session_link) {
        setSessionLink(response.session_link);
      } else {
        onError?.("Respuesta inesperada del servidor.");
      }
    } catch (error) {
      onError?.(
        getApiErrorMessage(
          error,
          "Error al generar la videollamada. Intenta de nuevo."
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenLink = () => {
    if (sessionLink) {
      window.open(sessionLink, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Modal activeModal={activeModal} handleModal={handleModal}>
      <Box textAlign="center" px={3} pb={3}>
        <Typography variant="h5" component="h2" sx={{ color: "#161e55", fontWeight: 700 }}>
          Generar videollamada
        </Typography>

        {!sessionLink ? (
          <Box width="80%" margin="1rem auto">
            <Box mt={3}>
              <FormControl fullWidth>
                <TextField
                  id="videollamada-name"
                  label="Nombre"
                  variant="outlined"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  error={Boolean(nameError)}
                  helperText={nameError}
                  disabled={loading}
                />
              </FormControl>
            </Box>
            <Box mt={3}>
              <FormControl fullWidth>
                <TextField
                  id="videollamada-document"
                  label="Número de documento"
                  variant="outlined"
                  value={documentNumber}
                  onChange={(event) => setDocumentNumber(event.target.value)}
                  error={Boolean(documentError)}
                  helperText={documentError}
                  disabled={loading}
                />
              </FormControl>
            </Box>
            <Box mt={4} display="flex" justifyContent="center" gap={2}>
              <ButtonSecondary variant="outlined" onClick={handleModal} disabled={loading}>
                Cancelar
              </ButtonSecondary>
              <ButtonAdd onClick={handleGenerate} disabled={loading}>
                {loading ? <CircularProgress size={22} color="inherit" /> : "Generar"}
              </ButtonAdd>
            </Box>
          </Box>
        ) : (
          <Box width="80%" margin="1rem auto">
            <Typography variant="body1" sx={{ mt: 3, mb: 2 }}>
              Enlace de sesión generado:
            </Typography>
            <TextField
              fullWidth
              value={sessionLink}
              InputProps={{ readOnly: true }}
              multiline
              minRows={2}
            />
            <Box mt={4} display="flex" justifyContent="center" gap={2}>
              <ButtonSecondary variant="outlined" onClick={handleModal}>
                Cerrar
              </ButtonSecondary>
              <ButtonAdd onClick={handleOpenLink}>Abrir en nueva pestaña</ButtonAdd>
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  );
}
